import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle, ArrowLeft, KeyRound, Database, UserPlus } from "lucide-react";

export default function AdminForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [noAccount, setNoAccount] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setSubmitting(true);
    setNoAccount(false);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 404) {
        // No account found
        setNoAccount(true);
      } else {
        toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // No account found state
  if (noAccount) {
    return (
      <div className="min-h-screen bg-background flex">
        {/* Left Panel */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">No Account Found</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't find an account with <span className="font-medium text-foreground">{email}</span>. 
              Would you like to create one?
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/register">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  Create an Account
                </Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={() => setNoAccount(false)}
                className="w-full"
              >
                Try a Different Email
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Branding */}
        <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/5 to-primary/10 items-center justify-center p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Database className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-3">Paeora</h2>
            <p className="text-muted-foreground max-w-sm">
              Share data, not seats. Create branded client portals from your Airtable bases.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex">
        {/* Left Panel */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Check Your Email</h1>
            <p className="text-muted-foreground mb-6">
              If an account exists with that email address, we've sent a password reset link. Please check your inbox and spam folder.
            </p>
            <Link to="/login">
              <Button className="bg-primary hover:bg-primary/90">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>

        {/* Right Panel - Branding */}
        <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/5 to-primary/10 items-center justify-center p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Database className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-3">Paeora</h2>
            <p className="text-muted-foreground max-w-sm">
              Share data, not seats. Create branded client portals from your Airtable bases.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Database className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold text-foreground">Paeora</span>
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Forgot Password?
            </h1>
            <p className="text-muted-foreground">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-foreground/80">Email Address</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-10 bg-secondary/50 border-border"
                  data-testid="admin-forgot-email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 mt-6" 
              disabled={submitting}
              data-testid="admin-send-reset-link-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Send Reset Link
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
              data-testid="admin-back-to-login-link"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/5 to-primary/10 items-center justify-center p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Database className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-3">Share data, not seats</h2>
          <p className="text-muted-foreground max-w-sm">
            Create branded client portals from your Airtable bases. No extra seats required, no complex setup needed.
          </p>
        </div>
      </div>
    </div>
  );
}
